import React from 'react';

import { T, FONT_STACK } from '../customer/customers.theme';

/**
 * The pieces Settings is built from, and the type scale it uses.
 *
 * ── Why this screen has its own scale ────────────────────────────────────
 *
 * The customers book and the menu are lists: dozens of rows, read at a glance,
 * scanned rather than read. Small type is right there. Settings is the
 * opposite — a page of prose explaining what each switch actually does to the
 * restaurant, read once, carefully, often by someone who is not sure. At 12.5
 * that prose was being skipped, which defeats the point of writing it.
 *
 * So everything is up roughly a point: body 12.5 → 13.5, labels 12.5 → 13,
 * inputs 14 → 15. Enough to read comfortably, not enough to look like a
 * different app sitting next to the other tabs.
 */
export const TYPE = {
  pageTitle: 19,
  sectionTitle: 16,
  body: 13.5,
  label: 13,
  input: 15,
  hint: 12.5,
  button: 14,
};

export const Card = ({ children, style }) => (
  <div
    style={{
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: T.radiusPanel,
      padding: 22,
      marginBottom: 16,
      ...style,
    }}
  >
    {children}
  </div>
);

export const SectionHead = ({ title, body, style }) => (
  <div style={{ marginBottom: 18, ...style }}>
    <div style={{ fontSize: TYPE.sectionTitle, fontWeight: 700 }}>{title}</div>
    {body ? (
      <div
        style={{
          fontSize: TYPE.body,
          lineHeight: 1.6,
          color: T.inkSoft,
          marginTop: 5,
          maxWidth: 580,
        }}
      >
        {body}
      </div>
    ) : null}
  </div>
);

export const Hint = ({ children, style }) =>
  children ? (
    <div style={{ fontSize: TYPE.hint, color: T.inkFaint, marginTop: 8, ...style }}>
      {children}
    </div>
  ) : null;

export const FieldError = ({ children }) =>
  children ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 7,
        fontSize: TYPE.hint,
        fontWeight: 600,
        color: T.danger,
      }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke={T.danger} strokeWidth="2" />
        <path d="M12 7v6" stroke={T.danger} strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="16.6" r="1.1" fill={T.danger} />
      </svg>
      <span>{children}</span>
    </div>
  ) : null;

/**
 * A text box.
 *
 * The same pill the rest of the app uses, restated here rather than imported
 * so this screen can carry its own type scale, a suffix (the % on VAT) and a
 * multi-line variant without changing how every other form in the till looks.
 */
export function Field({
  label,
  name,
  value,
  onChange,
  placeholder,
  invalid,
  disabled,
  suffix,
  inputMode,
  rows,
  maxLength,
  align,
  trailing,
}) {
  const shell = {
    display: 'flex',
    // Without this an input's intrinsic width (about 170px) holds the pill
    // open and a narrow flex-basis is quietly ignored.
    minWidth: 0,
    alignItems: rows ? 'flex-start' : 'center',
    gap: 10,
    minHeight: 48,
    padding: rows ? '12px 16px' : '0 16px',
    background: disabled ? T.sunk : T.surface,
    border: `${invalid ? 1.5 : 1}px solid ${invalid ? T.brand : T.borderStrong}`,
    borderRadius: rows ? T.radiusControl : T.radiusPill,
    boxShadow: invalid ? `0 0 0 3px ${T.brandTint}` : 'none',
    boxSizing: 'border-box',
  };

  const inner = {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    font: 'inherit',
    fontFamily: FONT_STACK,
    fontSize: TYPE.input,
    lineHeight: rows ? 1.55 : 'normal',
    color: T.ink,
    resize: 'none',
    textAlign: align || 'left',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      {label ? (
        <label htmlFor={name} style={{ fontSize: TYPE.label, fontWeight: 600, color: T.ink }}>
          {label}
        </label>
      ) : null}

      <div style={shell}>
        {rows ? (
          <textarea
            id={name}
            name={name}
            rows={rows}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={(event) => onChange(event.target.value)}
            style={inner}
          />
        ) : (
          <input
            id={name}
            name={name}
            type="text"
            inputMode={inputMode}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={(event) => onChange(event.target.value)}
            style={inner}
          />
        )}

        {suffix ? (
          <span style={{ fontSize: TYPE.input, color: T.inkFaint, flexShrink: 0 }}>{suffix}</span>
        ) : null}
        {trailing || null}
      </div>
    </div>
  );
}

/** A switch, because a checkbox reads as a form field and this is a state. */
export function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        position: 'relative',
        width: 44,
        height: 26,
        padding: 0,
        background: checked ? T.brand : T.borderStrong,
        border: 'none',
        borderRadius: T.radiusPill,
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0,
        transition: 'background 120ms linear',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: checked ? 21 : 3,
          width: 20,
          height: 20,
          background: T.surface,
          borderRadius: T.radiusPill,
          transition: 'left 120ms ease',
        }}
      />
    </button>
  );
}

export const SwitchRow = ({ label, body, checked, onChange, disabled, divider }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 20,
      paddingTop: divider ? 18 : 0,
      borderTop: divider ? `1px solid ${T.hairline}` : 'none',
      marginTop: divider ? 18 : 0,
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: TYPE.body + 1, fontWeight: 600 }}>{label}</div>
      <div
        style={{
          fontSize: TYPE.body,
          lineHeight: 1.6,
          color: T.inkSoft,
          marginTop: 4,
          maxWidth: 560,
        }}
      >
        {body}
      </div>
    </div>
    <div style={{ paddingTop: 2 }}>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} label={label} />
    </div>
  </div>
);

/** An amber line for something that is about to happen, not something wrong. */
export const WarnLine = ({ children }) => (
  <div
    style={{
      marginTop: 14,
      padding: '11px 14px',
      background: T.pendingTint,
      border: `1px solid ${T.pendingBorder}`,
      borderRadius: T.radiusControl,
      fontSize: TYPE.body,
      lineHeight: 1.6,
      color: T.pendingInk,
      maxWidth: 580,
    }}
  >
    {children}
  </div>
);

export const EmptyNote = ({ children }) => (
  <div
    style={{
      padding: '18px 16px',
      background: T.sunk,
      border: `1px dashed ${T.borderStrong}`,
      borderRadius: T.radiusControl,
      fontSize: TYPE.body,
      color: T.inkSoft,
    }}
  >
    {children}
  </div>
);

export function SmallButton({ children, onClick, disabled, icon, tone, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        height: 40,
        padding: '0 16px',
        background: T.surface,
        border: `1px solid ${tone === 'danger' ? T.dangerBorder : T.borderStrong}`,
        borderRadius: T.radiusControl,
        font: 'inherit',
        fontFamily: FONT_STACK,
        fontSize: TYPE.label,
        fontWeight: 600,
        color: disabled ? T.inkFaint : tone === 'danger' ? T.danger : T.ink,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

export const PlusGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

export const BinGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 7h16M10 4h4M6 7l1 13h10l1-13M10 11v6M14 11v6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The bin at the end of a repeated row.
 *
 * `topNudge` matches the height of a label above the first row's field, so the
 * bin sits on the middle of the pill rather than three pixels above it.
 */
export const RowBin = ({ onClick, disabled, label, topNudge }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 48,
      height: 48,
      marginTop: topNudge || 0,
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: T.radiusControl,
      color: T.inkFaint,
      cursor: disabled ? 'not-allowed' : 'pointer',
      flexShrink: 0,
    }}
  >
    <BinGlyph />
  </button>
);

/** How far a first row's field sits below the top of its column heading. */
export const LABEL_HEIGHT = 25;
